import React, { useEffect, useState } from "react";
import { Box, Input, Button, List, ListItem, VStack, HStack, Spinner, Text } from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import { useAuth } from "../contexts/AuthContext";
import tickers from "../data/tickers.json";
import { useToast } from "@chakra-ui/react";


const Watchlist = ({ onTickerClick }) => {
  const { user, addTickerToWatchlist, removeTickerFromWatchlist, askAI, setUser } = useAuth();

  const [watchlist, setWatchlist] = useState([]);
  const [input, setInput] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    setWatchlist(user?.watchlist || []);
  }, [user]);

  const handleInputChange = (e) => {
    const val = e.target.value.toUpperCase();
    setInput(val);
    if (!val) {
      setSuggestions([]);
      return;
    }
    const matches = tickers
      .filter(
        (t) =>
          t?.ticker?.toUpperCase().startsWith(val) ||
          t?.name?.toUpperCase().startsWith(val)
      )
      .slice(0, 10);
    setSuggestions(matches);
  };

  // Updated handleAdd
  const handleAdd = async (ticker) => {
    if (!ticker || watchlist.includes(ticker)) return;
    setLoading(true);
    try {
      await addTickerToWatchlist([ticker]);
      setWatchlist(prev => [...prev, ticker]);
      setInput("");
      setSuggestions([]);
    } catch (err) {
      const message =
        err.response?.data?.error || err.message || "Failed to add ticker";

      // Show Chakra toast
      toast({
        title: "Error",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (ticker) => {
    setLoading(true);
    try {
      await removeTickerFromWatchlist(ticker);
      setWatchlist(prev => prev.filter(t => t !== ticker));
    } catch (err) {
      console.error("Remove error:", err);
      alert(err.response?.data?.error || err.message || "Failed to remove ticker");
    } finally {
      setLoading(false);
    }
  };

  const handleAskAI = async (ticker) => {
    if (!ticker) return;
    setAiLoading(true);
    try {
      const result = await askAI(ticker);
      // // Increment locally in context
      // setUser(prev => ({
      //   ...prev,
      //   aiQueriesUsed: (prev?.aiQueriesUsed || 0) + 1,
      // }));
      onTickerClick?.(ticker, result);
    } catch (err) {
      console.error("AI query failed:", err);
      alert("Failed to ask AI");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <Box>
      <VStack align="stretch" spacing={3}>
        <Input
          placeholder="Search ticker or company"
          value={input}
          onChange={handleInputChange}
        />
        {suggestions.length > 0 && (
          <List spacing={1} bg="gray.50" p={2} borderRadius="md" maxH="200px" overflowY="auto">
            {suggestions.map((item) => (
              <ListItem key={item.ticker}>
                <Button
                  size="sm"
                  leftIcon={<AddIcon />}
                  width="100%"
                  justifyContent="start"
                  onClick={() => handleAdd(item.ticker)}
                >
                  {item.ticker} - {item.name}
                </Button>
              </ListItem>
            ))}
          </List>
        )}
      </VStack>

      <Box mt={4}>
        <Text fontWeight="bold">📌 Your Watchlist</Text>
        {loading ? (
          <HStack mt={2}>
            <Spinner />
            <Text>Loading...</Text>
          </HStack>
        ) : (
          <VStack align="stretch" spacing={2} mt={2}>
            {watchlist.map((t) => (
              <HStack key={t} justify="space-between">
                <Button
                  variant="link"
                  colorScheme="teal"
                  onClick={() => handleAskAI(t)}
                  isLoading={aiLoading}
                >
                  {t}
                </Button>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="outline"
                  onClick={() => handleRemove(t)}
                >
                  ❌ Remove
                </Button>
              </HStack>
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default Watchlist;
